import React, { useState, useEffect } from 'react';
import { Button, Input } from './ui';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, AlertCircle, Clock, Mail, Lock, LogOut } from 'lucide-react';
import Logo from './Logo';

const LoginScreen = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, error, isBlocked, blockTimeRemaining, logoutReason, deveTrocarSenha } = useAuth();
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInactivityMessage, setShowInactivityMessage] = useState(false);

  // Mostrar mensagem se o logout foi por inatividade
  useEffect(() => {
    if (logoutReason === 'inactivity') {
      setShowInactivityMessage(true);
      // Esconder após 10 segundos
      const timer = setTimeout(() => setShowInactivityMessage(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [logoutReason]);

  // Redirecionar se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      // Se precisa trocar a senha, redirecionar para a tela de troca obrigatória
      if (deveTrocarSenha) {
        navigate('/trocar-senha-obrigatoria');
      } else {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, deveTrocarSenha, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (isBlocked) return;
    
    setIsSubmitting(true);
    
    try {
      await login(formData.username, formData.password, formData.rememberMe);
      // O useEffect cuida do redirecionamento baseado no estado isAuthenticated e deveTrocarSenha
    } catch (error) {
      console.error('Erro no login:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Formata milissegundos para MM:SS
  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-green-400 to-blue-500">
      <div className="grid grid-cols-1 md:grid-cols-2 w-full max-w-5xl shadow-lg rounded-lg overflow-hidden">
        <div className="flex flex-col items-center justify-center bg-white p-10">
          <img
            src="/assets/images/logotipo.png"
            alt="Logo IBE"
            className="h-32 w-32 sm:h-40 sm:w-40 md:h-48 md:w-48 mb-6 object-contain"
          />
          <form className="w-full max-w-md" onSubmit={handleLogin}>
            {/* Mensagem de logout por inatividade */}
            {showInactivityMessage && (
              <div className="mb-4 p-3 bg-amber-100 border border-amber-400 text-amber-700 rounded-lg flex items-center">
                <LogOut className="w-5 h-5 mr-2" />
                <span className="text-sm">Você foi desconectado por inatividade. Faça login novamente.</span>
              </div>
            )}
            
            {/* Mensagem de erro */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
                <AlertCircle className="w-5 h-5 mr-2" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Mensagem de bloqueio */}
            {isBlocked && (
              <div className="mb-4 p-4 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded-lg">
                <div className="flex items-center mb-2">
                  <Clock className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Conta temporariamente bloqueada</span>
                </div>
                <p className="text-sm mb-2">
                  Muitas tentativas de login incorretas. Tente novamente em:
                </p>
                <div className="font-mono text-lg font-bold">
                  {formatTime(blockTimeRemaining)}
                </div>
              </div>
            )}

            <div className="mb-6">
              <label
                htmlFor="username"
                className="block text-lg font-medium text-gray-700 mb-2"
              >
                Username
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="w-full pl-10 px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Digite seu username"
                  disabled={isBlocked || isSubmitting}
                  required
                />
              </div>
            </div>
            <div className="mb-6">
              <label
                htmlFor="password"
                className="block text-lg font-medium text-gray-700 mb-2"
              >
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-10 px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Digite sua senha"
                  disabled={isBlocked || isSubmitting}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  disabled={isBlocked || isSubmitting}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="flex justify-between items-center mb-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  disabled={isBlocked || isSubmitting}
                  className="mr-2 rounded border-gray-300 text-green-600 shadow-sm focus:border-green-300 focus:ring focus:ring-green-200 focus:ring-opacity-50"
                />
                <span className="text-sm text-gray-600">Lembrar-me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-green-500 hover:underline">
                Esqueceu a Senha?
              </Link>
            </div>
            <Button
              type="submit"
              disabled={isBlocked || isSubmitting || isLoading}
              className="w-full bg-green-500 text-white py-3 px-6 rounded-lg text-lg font-semibold hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting || isLoading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
        </div>
        <div className="hidden md:block bg-cover bg-center" style={{ backgroundImage: 'url(/assets/images/background-login.png)' }}>
          {/* Background image section */}
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
